import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const SCRIPT_STRUCTURE = [
  {
    step: "1",
    title: "The hook (0-3 seconds)",
    description:
      "Lead with the property's strongest selling point. A striking feature, the location, or a bold price statement. You have three seconds to stop the scroll.",
    example: "\"Clifton penthouse. Panoramic harbour views. Guide price: 850,000.\"",
  },
  {
    step: "2",
    title: "The setting (3-8 seconds)",
    description:
      "Establish the location and lifestyle. Mention the neighbourhood, nearby amenities, transport links, or schools. Give the viewer a reason to care about where this property is.",
    example: "\"Steps from the Suspension Bridge, five minutes to the village, with Waitrose and top-rated schools nearby.\"",
  },
  {
    step: "3",
    title: "Key features (8-20 seconds)",
    description:
      "Walk through the standout rooms and features. Focus on what makes this property different: period details, a new kitchen, garden size, parking, or energy efficiency. Keep sentences short.",
    example: "\"Four bedrooms. Open-plan kitchen-diner with bi-fold doors to the south-facing garden. Principal suite with dressing room and en-suite.\"",
  },
  {
    step: "4",
    title: "The lifestyle close (20-25 seconds)",
    description:
      "Paint a picture of life in this home. Help the viewer imagine themselves living there. This is what turns a feature list into an emotional connection.",
    example: "\"Weekend mornings on the terrace with coffee. Evening walks along the harbourside. A home that fits how you actually live.\"",
  },
  {
    step: "5",
    title: "The call to action (25-30 seconds)",
    description:
      "End with a clear next step. Include the agent name, a prompt to get in touch, and urgency if appropriate. Keep it conversational, not salesy.",
    example: "\"Book your viewing with Savills Clifton today. This one won't last long.\"",
  },
]

const TIPS = [
  {
    title: "Write for the ear, not the eye",
    description:
      "Scripts are spoken aloud. Read every line out loud before finalising. If it sounds awkward, rewrite it. Short sentences work best. Avoid jargon like 'comprising' or 'benefitting from'.",
  },
  {
    title: "Lead with benefits, not features",
    description:
      "'South-facing garden perfect for summer entertaining' beats 'south-facing garden, approximately 40ft'. People buy the lifestyle, not the measurements.",
  },
  {
    title: "Match the template tone",
    description:
      "A Prestige template calls for measured, confident language. A Modern template can be punchier and more conversational. The script and visuals should feel like they belong together.",
  },
  {
    title: "Keep it under 90 words",
    description:
      "A 30-second video needs roughly 75-90 words. That is about 5-7 sentences. Every word must earn its place. If a sentence doesn't add information or emotion, cut it.",
  },
  {
    title: "Use numbers and specifics",
    description:
      "'Four bedrooms' is stronger than 'spacious family home'. '1,800 sq ft' is more convincing than 'generous proportions'. Specifics build trust.",
  },
  {
    title: "Avoid cliches",
    description:
      "'Deceptively spacious', 'must be seen to be appreciated', and 'no onward chain' belong in written particulars, not video scripts. Write the way you would actually describe the property to a friend.",
  },
]

const COMMON_MISTAKES = [
  { mistake: "Too many features", fix: "Pick the top 3-5. More detail dilutes impact." },
  { mistake: "No location context", fix: "Always include the area and what makes it desirable." },
  { mistake: "Generic opening", fix: "Start with what makes this property unique, not 'Welcome to...'." },
  { mistake: "Missing CTA", fix: "Every script needs a clear call to action at the end." },
  { mistake: "Corporate tone", fix: "Write conversationally. 'You' and 'your' make it personal." },
  { mistake: "Too long", fix: "Cut ruthlessly. 30-second videos work best on social media." },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "How long should a property video script be?",
    a: "For social media video ads, aim for 75-90 words (about 30 seconds). This is long enough to cover the key selling points and short enough to hold attention on Instagram, TikTok, and Facebook. Longer videos (60-90 seconds) work for YouTube and your website, but the 30-second format consistently gets the best engagement.",
  },
  {
    q: "Do I need to write a script for every listing?",
    a: "If you are using QuiteRight, the AI generates a script automatically from the listing description. You can edit it to add local knowledge or adjust the tone, but most agents find the generated scripts are ready to use. For premium listings, spending 5 minutes refining the script is worthwhile.",
  },
  {
    q: "What makes a good property video script different from written particulars?",
    a: "Written particulars are comprehensive and factual. Video scripts are selective and emotional. A good script picks the 3-5 most compelling features and presents them as benefits. It reads naturally when spoken aloud and creates a feeling, not just a list.",
  },
  {
    q: "Should I include the price in the video script?",
    a: "Usually, yes. Including the price qualifies viewers immediately and avoids wasted enquiries. For properties where the price is a strong selling point (well-priced for the area, recently reduced), lead with it. For premium properties where the lifestyle is the draw, you can mention the price later or use 'guide price' to signal flexibility.",
  },
  {
    q: "Can I use the same script structure for lettings?",
    a: "Yes. The five-part structure (hook, setting, features, lifestyle, CTA) works for lettings too. Adjust the language: 'available from' instead of 'guide price', 'move-in date' instead of 'completion', and emphasise transport links and local amenities that appeal to renters.",
  },
  {
    q: "How do I write scripts for different social media platforms?",
    a: "The core script stays the same. For TikTok and Reels, front-load the hook because swipe-away happens in 1-2 seconds. For Facebook, a slightly longer format (45 seconds) works because users scroll more slowly. For LinkedIn, focus on the investment angle or market context. QuiteRight renders videos for all platforms from a single script.",
  },
  {
    q: "What tone should a property video script use?",
    a: "Confident and conversational. Avoid estate agent cliches ('deceptively spacious', 'must be seen') and corporate jargon ('comprising', 'benefitting from'). Write the way you would describe the property to a friend who asked 'What's it like?'. The best scripts sound natural, not scripted.",
  },
  {
    q: "How does QuiteRight generate video scripts?",
    a: "QuiteRight reads the listing description, photos, and property details, then generates a concise video script tailored to the template you have chosen. The script is optimised for voiceover: short sentences, natural phrasing, and a clear structure. You can edit the script before rendering if you want to add personal touches or local knowledge.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <div className="divide-y divide-stone">
      {items.map((item) => (
        <details key={item.q} className="group py-5">
          <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
            {item.q}
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
            >
              <path
                fillRule="evenodd"
                d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </summary>
          <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
        </details>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function PropertyVideoScriptWritingGuide({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How to Write Property Video Scripts That Sell",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "Property Video Script Writing" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Property video script writing
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            How to write property video scripts that stop the scroll and generate
            enquiries. A five-part structure, practical tips, and common mistakes
            to avoid.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Why scripts matter */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Why the script matters more than the footage
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Most property videos fail because they focus on images and ignore
              the words. A beautiful slideshow with no narrative is wallpaper.
              The script is what turns a collection of photos into a story that
              makes someone pick up the phone.
            </p>
            <p>
              A good script does three things: it hooks attention in the first
              three seconds, it highlights the features that matter most to the
              target buyer, and it ends with a clear call to action. Get these
              three right and the video will outperform every other piece of
              marketing on the listing.
            </p>
            <p>
              The good news: you already know how to describe properties. You do
              it every day at viewings and valuations. Writing a video script is
              just that same skill, condensed into 30 seconds.
            </p>
          </div>
        </div>
      </section>

      {/* Five-part structure */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Script structure
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              The five-part property video script
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-sm text-slate">
              This structure works for 30-second social media ads, 60-second
              portal videos, and everything in between. Adjust the word count
              for each section based on your target length.
            </p>
          </ScrollReveal>

          <div className="mt-12 space-y-4">
            {SCRIPT_STRUCTURE.map((step) => (
              <ScrollReveal key={step.step}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <div className="flex items-start gap-4">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-red font-display text-lg font-bold text-white">
                      {step.step}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-display text-lg font-bold text-charcoal">
                        {step.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate">
                        {step.description}
                      </p>
                      <p className="mt-3 rounded-lg bg-cream px-4 py-3 text-sm italic text-charcoal">
                        {step.example}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Writing tips */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Best practices
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Six rules for better scripts
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TIPS.map((tip, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-base font-bold text-charcoal">
                  {tip.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {tip.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Common mistakes */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Avoid these
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common script mistakes
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 overflow-x-auto rounded-xl border border-stone">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone bg-white">
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Mistake
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Fix
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone">
                {COMMON_MISTAKES.map((row) => (
                  <tr key={row.mistake} className="bg-white hover:bg-cream/30">
                    <td className="px-4 py-3 font-medium text-charcoal">
                      {row.mistake}
                    </td>
                    <td className="px-4 py-3 text-slate">{row.fix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "75-90", label: "words for a 30-second script" },
            { stat: "3s", label: "to hook attention or lose the viewer" },
            { stat: "2x", label: "more enquiries from listings with video" },
            { stat: "60s", label: "to create a video with QuiteRight" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              { href: "/guides/property-video-marketing", title: "Property video marketing guide", desc: "The complete guide to video marketing for estate agents." },
              { href: "/guides/social-media-estate-agents", title: "Social media for estate agents", desc: "Platform strategies for Instagram, TikTok, Facebook, and more." },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Read guide
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 transition-transform group-hover:translate-x-0.5">
                    <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Skip the blank page
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            QuiteRight generates property video scripts automatically from your
            listing. Paste a URL, pick a template, and your video is ready in 60
            seconds. Script included.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
